import { ReadonlyVec3 } from 'gl-matrix';
import { StructureSetId } from '../changes/ids';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { DicomColor } from '../dicom/dicomColor';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import {
    RoiGenerationAlgorithmType,
    RoiIdentificationCodeSequence,
    RoiInterpretedType,
} from '../dicom/dicomstructureset';
import { StructureSet } from '../patient/structureset';

/**
 * Immutable ROI read model for React / Recoil consumption.
 */
export interface RoiReadModel {
    readonly roiNumber: number;
    readonly name: string;
    readonly color: Readonly<DicomColor>;
    readonly visible: boolean;
    readonly contourThickness: number;
    readonly approvalStatus: DICOMApprovalStatus;
    readonly interpretedType: RoiInterpretedType;
    readonly electronDensityOverride: number | undefined;
    readonly massDensityOverride: number | undefined;
    readonly roiDescription: string;
    readonly roiGenerationAlgorithm: RoiGenerationAlgorithmType;
    readonly contourCount: number;
    readonly isApproved: boolean;
    readonly isPointRoi: boolean;
    readonly identificationCode: RoiIdentificationCodeSequence | undefined;
    /** Single-point coordinate for POINT contours (markers, isocenters, etc.) in mm */
    readonly pointCoordinatesMm: ReadonlyVec3 | null;
}

/**
 * Immutable StructureSet read model for React / Recoil consumption.
 */
export interface StructureSetReadModel {
    /** Branded domain identifier — pass to services / useLiveStructureSet. */
    readonly id: StructureSetId;
    readonly dicomUid: string;
    readonly structureSetLabel: string;
    readonly structureSetDescription: string;
    readonly approvalStatus: DICOMApprovalStatus;
    readonly isApproved: boolean;
    readonly reviewerName: string;
    readonly reviewDate: string;
    readonly reviewTime: string;
    readonly frameOfReferenceUid: string;
    readonly numberOfStructures: number;
    readonly roiArray: ReadonlyArray<RoiReadModel>;
    readonly manufacturer: string;
    readonly manufacturerModelName: string;
    readonly softwareVersions: string;
    readonly hasImage: boolean;
    readonly imageUid: string | null;
    readonly generationModel?: GenerationModelDetails;
    /** True for structure sets created in this session (not yet persisted). */
    readonly isNew: boolean;
    /** True once the structure set has been exported / uploaded. */
    readonly isExported: boolean;
}

/**
 * Builds an immutable {@link StructureSetReadModel} from a live
 * {@link StructureSet} domain object.
 */
export function createStructureSetReadModel(
    structureSet: StructureSet
): StructureSetReadModel {
    const roiReadModels: RoiReadModel[] = structureSet.roiArray.map((roi) =>
        Object.freeze({
            roiNumber: roi.roiNumber,
            name: roi.name,
            roiDescription: roi.roiDescription,
            color: Object.freeze({ ...roi.color }),
            visible: roi.visible,
            contourThickness: roi.contourThickness,
            approvalStatus: roi.approvalStatus,
            interpretedType: roi.interpretedType,
            electronDensityOverride: roi.electronDensityOverride,
            massDensityOverride: roi.massDensityOverride,
            roiGenerationAlgorithm: roi.roiGenerationAlgorithm,
            contourCount: roi.contours.length,
            isApproved: roi.isApproved,
            isPointRoi: roi.isPointRoi,
            identificationCode: roi.identificationCode,
            pointCoordinatesMm: roi.pointCoordinatesMm,
        })
    );

    return Object.freeze({
        id: structureSet.id,
        dicomUid: structureSet.dicomUid,
        structureSetLabel: structureSet.structureSetLabel,
        structureSetDescription: structureSet.structureSetDescription,
        approvalStatus: structureSet.approvalStatus,
        isApproved: structureSet.isApproved,
        reviewerName: structureSet.reviewerName,
        reviewDate: structureSet.reviewDate,
        reviewTime: structureSet.reviewTime,
        frameOfReferenceUid: structureSet.frameOfReferenceUid,
        numberOfStructures: structureSet.numberOfStructures,
        roiArray: Object.freeze(roiReadModels),
        manufacturer: structureSet.manufacturer,
        manufacturerModelName: structureSet.manufacturerModelName,
        softwareVersions: structureSet.softwareVersions,
        hasImage: structureSet.image !== null,
        imageUid: structureSet.image?.dicomUid ?? null,
        generationModel: structureSet.generationModel,
        isNew: structureSet.isNew,
        isExported: structureSet.isExported,
    });
}
